<template>
  <vs-popup
    class="audio-presentation-popup"
    title="Áudio de apresentação"
    :active.sync="internalActive"
    :button-close-hidden="isUploading"
  >
    <div class="audio-presentation">
      <p class="audio-presentation-intro">
        Grave um áudio curto de até <strong>2 minutos</strong> se apresentando.
        Esse áudio fica no seu perfil e é exibido para todas as empresas em
        qualquer vaga que você se candidatar — não precisa gravar para cada
        oportunidade. Conte um pouco quem você é, sua trajetória e o que procura agora.
      </p>

      <div v-if="permissionError" class="audio-presentation-error">
        <p>
          Não conseguimos acessar o microfone. Verifique nas configurações do
          navegador se o acesso ao microfone está permitido para esta página.
        </p>
      </div>

      <div class="audio-presentation-stage">
        <div v-if="state === 'idle'" class="audio-presentation-idle">
          <button
            type="button"
            class="audio-presentation-record-btn"
            :disabled="permissionError"
            @click="startRecording"
          >
            <span class="audio-presentation-record-dot"></span>
            Iniciar gravação
          </button>
          <p v-if="hasExisting" class="audio-presentation-warning">
            Iniciar uma nova gravação substituirá o áudio salvo atualmente.
          </p>
        </div>

        <div v-else-if="state === 'recording'" class="audio-presentation-recording">
          <div class="audio-presentation-pulse"></div>
          <div class="audio-presentation-timer">
            {{ formatTime(elapsed) }} <span class="audio-presentation-timer-max">/ 02:00</span>
          </div>
          <button
            type="button"
            class="audio-presentation-stop-btn"
            @click="stopRecording"
          >
            Parar gravação
          </button>
        </div>

        <div v-else-if="state === 'recorded'" class="audio-presentation-recorded">
          <audio v-if="previewUrl" :src="previewUrl" controls class="audio-presentation-preview"></audio>
          <p class="audio-presentation-recorded-meta">
            Duração: {{ formatTime(recordedDuration) }}
          </p>
          <div class="audio-presentation-actions">
            <button
              type="button"
              class="audio-presentation-secondary-btn"
              :disabled="isUploading"
              @click="discardAndRecordAgain"
            >
              Regravar
            </button>
            <button
              type="button"
              class="audio-presentation-primary-btn"
              :disabled="isUploading"
              @click="save"
            >
              {{ isUploading ? 'Salvando...' : 'Salvar áudio' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
const MAX_DURATION_SECONDS = 120;

export default {
  name: 'AudioPresentationModal',
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    hasExisting: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    state: 'idle', // idle | recording | recorded
    stream: null,
    recorder: null,
    chunks: [],
    blob: null,
    previewUrl: null,
    elapsed: 0,
    recordedDuration: 0,
    intervalId: null,
    permissionError: false,
    isUploading: false,
  }),
  computed: {
    internalActive: {
      get() {
        return this.active;
      },
      set(value) {
        this.$emit('update:active', value);
      },
    },
  },
  watch: {
    active(value) {
      if (value) {
        this.reset();
      } else {
        this.cleanup();
      }
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    reset() {
      this.cleanup();
      this.state = 'idle';
      this.elapsed = 0;
      this.recordedDuration = 0;
      this.permissionError = false;
      this.isUploading = false;
      this.blob = null;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },
    cleanup() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.recorder && this.recorder.state !== 'inactive') {
        try {
          this.recorder.stop();
        } catch (_) {}
      }
      this.recorder = null;
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
    async startRecording() {
      this.permissionError = false;
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        this.permissionError = true;
        return;
      }

      const mime = this.pickSupportedMimeType();
      try {
        this.recorder = mime
          ? new MediaRecorder(this.stream, { mimeType: mime })
          : new MediaRecorder(this.stream);
      } catch (error) {
        this.permissionError = true;
        this.cleanup();
        return;
      }

      this.chunks = [];
      this.elapsed = 0;
      this.state = 'recording';

      this.recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };
      this.recorder.onstop = () => {
        const type = (this.recorder && this.recorder.mimeType) || 'audio/webm';
        this.blob = new Blob(this.chunks, { type });
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
        }
        this.previewUrl = URL.createObjectURL(this.blob);
        this.recordedDuration = this.elapsed;
        this.state = 'recorded';
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
          this.stream = null;
        }
      };
      this.recorder.start();

      this.intervalId = setInterval(() => {
        this.elapsed += 1;
        if (this.elapsed >= MAX_DURATION_SECONDS) {
          this.stopRecording();
        }
      }, 1000);
    },
    stopRecording() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.recorder && this.recorder.state !== 'inactive') {
        try {
          this.recorder.stop();
        } catch (_) {}
      }
    },
    discardAndRecordAgain() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      this.blob = null;
      this.elapsed = 0;
      this.recordedDuration = 0;
      this.state = 'idle';
    },
    async save() {
      if (!this.blob || this.isUploading) return;
      this.isUploading = true;
      try {
        const ext = this.extensionFromMime(this.blob.type);
        const filename = `presentation.${ext}`;
        const file = new File([this.blob], filename, { type: this.blob.type });
        await this.$store.dispatch('presentation/saveAudio', file);
        this.$vs.notify({
          title: 'Áudio salvo!',
          text: 'Seu áudio de apresentação foi atualizado.',
          iconPack: 'feather',
          icon: 'icon-check-circle',
          color: 'success',
        });
        this.$emit('saved');
        this.internalActive = false;
      } catch (error) {
        const message =
          (error && error.response && error.response.data && error.response.data.message) ||
          'Não foi possível salvar o áudio. Tente novamente.';
        this.$vs.notify({
          title: 'Falha ao salvar',
          text: message,
          iconPack: 'feather',
          icon: 'icon-x-circle',
          color: 'danger',
        });
      } finally {
        this.isUploading = false;
      }
    },
    formatTime(totalSeconds) {
      const seconds = Math.max(0, Math.floor(totalSeconds || 0));
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    pickSupportedMimeType() {
      if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
        return null;
      }
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4'];
      for (const candidate of candidates) {
        if (MediaRecorder.isTypeSupported(candidate)) return candidate;
      }
      return null;
    },
    extensionFromMime(mime) {
      if (!mime) return 'webm';
      if (mime.includes('webm')) return 'webm';
      if (mime.includes('ogg')) return 'ogg';
      if (mime.includes('mp4')) return 'm4a';
      if (mime.includes('mpeg')) return 'mp3';
      if (mime.includes('wav')) return 'wav';
      return 'webm';
    },
  },
};
</script>

<style lang="scss">
.audio-presentation-popup {
  .vs-popup {
    max-width: 520px;
    width: 100%;
    border-radius: 16px;
  }
  .vs-popup--content {
    padding: 24px;
  }
}

.audio-presentation {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: Roboto, sans-serif;
  color: #333;

  &-intro {
    font-size: 14px;
    line-height: 1.5;
    color: #4f4f4f;
    margin: 0;

    strong {
      color: #333;
    }
  }

  &-error {
    background: rgba(222, 35, 72, 0.08);
    border: 0.5px solid #de2348;
    border-radius: 8px;
    padding: 12px;
    color: #de2348;
    font-size: 14px;
  }

  &-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    border-radius: 16px;
    background: #f7f8fa;
    border: 1px solid #ecebeb;
    min-height: 180px;
    justify-content: center;
  }

  &-idle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  &-record-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 20px;
    border-radius: 22px;
    background: #4169e1;
    color: #fff;
    font-weight: 500;
    font-size: 15px;
    border: none;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &-record-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fff;
  }

  &-warning {
    margin: 0;
    font-size: 13px;
    color: #af6c00;
    text-align: center;
  }

  &-recording {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &-pulse {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #de2348;
    animation: audio-presentation-pulse 1.4s ease-in-out infinite;
  }

  &-timer {
    font-size: 24px;
    font-weight: 600;
    color: #333;
    font-variant-numeric: tabular-nums;

    &-max {
      color: #696969;
      font-weight: 400;
      font-size: 18px;
    }
  }

  &-stop-btn {
    height: 40px;
    padding: 0 18px;
    border-radius: 8px;
    background: #fff;
    color: #de2348;
    border: 1px solid #de2348;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(222, 35, 72, 0.08);
    }
  }

  &-recorded {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  &-preview {
    width: 100%;
  }

  &-recorded-meta {
    margin: 0;
    color: #696969;
    font-size: 13px;
    text-align: center;
  }

  &-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  &-secondary-btn,
  &-primary-btn {
    height: 40px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &-secondary-btn {
    background: #fff;
    color: #4f4f4f;
    border: 0.5px solid #a8a8a8;
  }

  &-primary-btn {
    background: #4169e1;
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  }
}

@keyframes audio-presentation-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(222, 35, 72, 0.4);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 16px rgba(222, 35, 72, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(222, 35, 72, 0);
  }
}
</style>
